import { FC } from 'react';
import { Autocomplete, TextField, styled, Typography } from '@mui/material';

const StyledAutocomplete = styled(Autocomplete)({
  width: '100%',
  '& .MuiAutocomplete-listbox': {
    maxHeight: '350px',
    overflow: 'auto',
  },
}) as typeof Autocomplete;

const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'hasValue' && prop !== 'hasError',
})<{ hasValue?: boolean; hasError?: boolean }>(({ hasValue, hasError }) => ({
  '& .MuiOutlinedInput-root': {
    height: '40px',
    fontSize: '14px',
    fontFamily: 'Outfit',
    borderRadius: '4px',
    backgroundColor: 'var(--text-bg)',
    '& fieldset': {
      borderColor: hasError ? 'var(--error)' : 'var(--border)',
    },
    '&:hover fieldset': {
      borderColor: hasError ? 'var(--error)' : 'var(--primary-blue)',
    },
    '&.Mui-focused fieldset': {
      borderColor: hasError ? 'var(--error)' : 'var(--primary-blue)',
    },
    '&.Mui-disabled': {
      backgroundColor: 'var(--bg-color)',
    },
  },
  '& .MuiInputBase-input': {
    padding: '0 14px !important',
    fontSize: '14px',
    fontFamily: 'Outfit',
    fontWeight: 400,
    color: hasValue ? 'var(--primary-navy)' : 'var(--subtext)',
    '&::placeholder': {
      color: 'var(--subtext)',
      opacity: 1,
    },
  },
}));

interface Vendor {
  id: string;
  organizationName: string;
}

interface VendorAutocompleteProps {
  id: string;
  label: string;
  formikProps: any;
  options: Vendor[];
  width?: string;
  showLabel?: boolean;
}

const VendorAutocomplete: FC<VendorAutocompleteProps> = ({
  id,
  label,
  formikProps,
  options,
  width = '300px',
  showLabel = true,
}) => {
  const value =
    options.find((option) => option.id === formikProps.values[id]) || null;
  const hasError = formikProps.touched[id] && Boolean(formikProps.errors[id]);

  return (
    <div style={{ width }}>
      {showLabel && (
        <Typography
          fontFamily="Outfit"
          fontWeight="400"
          fontSize="16px"
          color="var(--primary-navy)"
          mb={2}
        >
          {label}
        </Typography>
      )}
      <StyledAutocomplete
        options={options}
        getOptionLabel={(option: any) => option.organizationName}
        value={value}
        onChange={(_, newValue: any) => {
          formikProps.setFieldValue(id, newValue?.id || '');
        }}
        onBlur={formikProps.handleBlur}
        renderInput={(params) => (
          <StyledTextField
            {...params}
            name={id}
            placeholder={`Select ${label}`}
            hasValue={!!formikProps.values[id]}
            hasError={hasError}
            error={hasError}
            helperText={hasError && formikProps.errors[id]}
            FormHelperTextProps={{
              style: {
                fontFamily: 'Outfit',
                fontSize: '12px',
                color: 'var(--error)',
                marginLeft: 0,
              },
            }}
          />
        )}
      />
    </div>
  );
};

export default VendorAutocomplete;
